import { useMemo } from "react";
import cloneDeep from "lodash/cloneDeep";
import { SelectOption } from "@iddataweb/idw-common-ui/types";
import { resources } from "@constants/hardcoded-strings";
import { USER_ROLE_TYPE_OPTIONS } from "@constants/user-role-type-options";
import { LayoutTypes } from "@enums/layout-types";
import { LabelWidthTypes } from "@enums/label-width-types";
import { AxnAdminUserPropertyBundleTypes } from "@enums/axn-admin-user-property-bundle-types";
import { UserPermissionTypes } from "@enums/user-permission-types";
import { getRelyingPartyRolesWithPermissions } from "@utils/permissions-util";
import { getAxnAdminUserPropertyBundleProperties } from "@utils/validation/organization/organization-users/organization-users-util";
import { RelyingPartyRole } from "@idw-types/common";
import { AxnAdminUserProperty } from "@idw-types/data/axn-admin-user/axn-admin-user-property";
import { AxnAdminUserPropertyBundle } from "@idw-types/data/axn-admin-user-property-bundle";
import { AxnAdminAvailableUserProperty } from "@idw-types/data/axn-admin-available-user-property";
import { AxnRelyingParty } from "@idw-types/data/reference-data/axn";
import { AxnAdminUser } from "@idw-types/data/axn-admin-user";
import { IDWSession } from "@idw-types/data/idw-session";
import { SelectFormGroup } from "@containers/common/select-form-group/select-form-group";
import { LabelValueContainer } from "@containers/common/label-value-container/label-value-container";
import { RelyingPartyRolesContainer } from "@containers/common/relying-party-roles-container/relying-party-roles-container";

type OrganizationUserDetailsRoleContainerProps = {
    isDisabled: boolean;
    labelWidth: LabelWidthTypes;
    session: IDWSession;
    axnAdminUserState: AxnAdminUser;
    adminProperty: AxnAdminUserProperty;
    organizationRole: AxnAdminUserPropertyBundle;
    isCustomOrgRole: boolean;
    relyingPartyRoles: Array<RelyingPartyRole>;
    axnRelyingParties: Array<AxnRelyingParty>;
    axnAdminUserPropertyBundles: Array<AxnAdminUserPropertyBundle>;
    axnAdminAvailableUserOrganizationProperties: Array<AxnAdminAvailableUserProperty>;
    axnAdminAvailableUserRPProperties: Array<AxnAdminAvailableUserProperty>;
    setAxnAdminUserState: (axnAdminUser: AxnAdminUser) => void;
};

export const OrganizationUserDetailsRoleContainer = (props: OrganizationUserDetailsRoleContainerProps) => {
    const {
        isDisabled,
        labelWidth,
        session,
        axnAdminUserState,
        adminProperty,
        isCustomOrgRole,
        organizationRole,
        relyingPartyRoles,
        axnAdminUserPropertyBundles,
        axnRelyingParties,
        axnAdminAvailableUserOrganizationProperties,
        axnAdminAvailableUserRPProperties,
        setAxnAdminUserState
    } = props;

    const handleRoleTypeChange = (newValue: SelectOption) => {
        const roleBundles = axnAdminUserPropertyBundles.filter(bundle => bundle.type === newValue.value);
        let newAxnAdminUserPropertyBundle = null;
        if (newValue.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION && roleBundles.length > 0) {
            newAxnAdminUserPropertyBundle = roleBundles[0];
        }

        const {
            axnAdminUserOrganizationProperties,
            axnAdminUserRPProperties
        } = getAxnAdminUserPropertyBundleProperties(
            newAxnAdminUserPropertyBundle,
            [],
            axnAdminAvailableUserOrganizationProperties,
            axnAdminAvailableUserRPProperties,
            axnAdminUserState
        );

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserOrganizationProperties,
            axnAdminUserRPProperties
        });
    };

    const handleOrganizationRoleChange = (newValue: SelectOption) => {
        const newAxnAdminUserPropertyBundle = axnAdminUserPropertyBundles.find(bundle => bundle.shortName === newValue.value);
        if (newAxnAdminUserPropertyBundle) {
            const {
                axnAdminUserOrganizationProperties,
                axnAdminUserRPProperties
            } = getAxnAdminUserPropertyBundleProperties(
                newAxnAdminUserPropertyBundle,
                [],
                axnAdminAvailableUserOrganizationProperties,
                axnAdminAvailableUserRPProperties,
                axnAdminUserState
            );

            setAxnAdminUserState({
                ...axnAdminUserState,
                axnAdminUserOrganizationProperties,
                axnAdminUserRPProperties
            });
        }
    };

    const addRelyingPartyRole = (relyingPartyRole: RelyingPartyRole) => {
        //get all of the rp properties for this relying party and bundle
        const {
            axnAdminUserOrganizationProperties,
            axnAdminUserRPProperties
        } = getAxnAdminUserPropertyBundleProperties(
            null,
            [relyingPartyRole],
            axnAdminAvailableUserOrganizationProperties,
            axnAdminAvailableUserRPProperties,
            axnAdminUserState
        );

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserOrganizationProperties,
            axnAdminUserRPProperties: [
                ...axnAdminUserState.axnAdminUserRPProperties,
                ...axnAdminUserRPProperties
            ]
        });
    };

    const removeRelyingPartyRole = (relyingPartyRole: RelyingPartyRole) => {
        //filter out rp properties for this relying party
        const newRpProperties = axnAdminUserState.axnAdminUserRPProperties
            .filter(property => property.relyingPartyID !== relyingPartyRole.relyingParty.id);

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserRPProperties: [...newRpProperties]
        });
    };

    const updateRelyingPartyRole = (relyingPartyRole: RelyingPartyRole) => {
        //remove all of the rp properties for this relying party and bundle, then insert the new properties
        const {
            axnAdminUserOrganizationProperties,
            axnAdminUserRPProperties
        } = getAxnAdminUserPropertyBundleProperties(
            null,
            [relyingPartyRole],
            axnAdminAvailableUserOrganizationProperties,
            axnAdminAvailableUserRPProperties,
            axnAdminUserState
        );

        const copiedArray = cloneDeep(axnAdminUserState.axnAdminUserRPProperties);

        const startIndex = copiedArray.findIndex(property => property.relyingPartyID === relyingPartyRole.relyingParty.id);
        const length = copiedArray.filter(property => property.relyingPartyID === relyingPartyRole.relyingParty.id).length;
        copiedArray.splice(startIndex, length, ...axnAdminUserRPProperties);

        setAxnAdminUserState({
            ...axnAdminUserState,
            axnAdminUserOrganizationProperties,
            axnAdminUserRPProperties: copiedArray
        });
    };

    const roleTypeOptions = useMemo(() => {
        if (axnAdminUserPropertyBundles.some(bundle => bundle.type === AxnAdminUserPropertyBundleTypes.ORGANIZATION)) {
            return USER_ROLE_TYPE_OPTIONS;
        }
        return [];
    }, [axnAdminUserPropertyBundles]);

    const selectedRoleType = useMemo(() => {
        if (organizationRole || isCustomOrgRole) {
            return USER_ROLE_TYPE_OPTIONS[0]
        } else if (adminProperty) {
            return { label: "IDDW Admin", value: "IDDW Admin" };
        }

        return USER_ROLE_TYPE_OPTIONS[1];
    }, [roleTypeOptions, isCustomOrgRole, organizationRole]);

    const axnAdminUserPropertyBundleOptions = useMemo(() => {
        return axnAdminUserPropertyBundles
            .filter(bundle => bundle.type === selectedRoleType?.value)
            .map(bundle => ({ label: bundle.name, value: bundle.shortName }));
    }, [axnAdminUserPropertyBundles, selectedRoleType]);

    const selectedOrganizationRole: SelectOption = useMemo(() => {
        if (organizationRole) {
            return {
                label: organizationRole.name,
                value: organizationRole.shortName
            };
        }

        return null;
    }, [organizationRole]);

    const relyingPartyRolesWithPermissions = useMemo(() => {
        return getRelyingPartyRolesWithPermissions(
            session,
            axnAdminUserState.id,
            axnRelyingParties,
            relyingPartyRoles
        )
    }, [relyingPartyRoles, axnRelyingParties, session]);

    const sessionUserHasAccessToAllAdminUserRPs = useMemo(() => {
        //get all of the relying party IDs the users being viewed has access to        
        const userRelyingPartyIds = relyingPartyRoles.map(rpr => rpr.relyingParty.id);

        //get all of the relying party IDs that the logged in user can view
        const sessionUserViewRelyingPartyIds = relyingPartyRolesWithPermissions.filter(rpr => rpr.canView).map(rpr => rpr.relyingPartyRole.relyingParty.id);

        //check to see if we have access to every relying party ID the viewed user has
        return userRelyingPartyIds.every(id => sessionUserViewRelyingPartyIds.includes(id));
    }, [
        relyingPartyRoles,
        relyingPartyRolesWithPermissions
    ]);

    const detailsResources = resources.pages.organizationUserDetails.tabs.details;

    return (
        <div>
            {(!!organizationRole || !!adminProperty || roleTypeOptions.length > 0) && (
                <SelectFormGroup
                    controlId={"user-role-type"}
                    automationId={"user-role-type"}
                    label={detailsResources.inputs.roleType.label}
                    ariaLabel={detailsResources.inputs.roleType.label}
                    disabled={isDisabled || !!adminProperty}
                    isClearable={false}
                    isRequired={true}
                    isSearchable={false}
                    labelWidth={labelWidth}
                    onChange={handleRoleTypeChange}
                    options={roleTypeOptions}
                    selectedOption={selectedRoleType}
                />
            )}
            {selectedRoleType.value === AxnAdminUserPropertyBundleTypes.ORGANIZATION && (
                <>
                    <SelectFormGroup
                        controlId={"organization-user-role"}
                        automationId={"organization-user-role"}
                        label={detailsResources.inputs.role.label}
                        ariaLabel={detailsResources.inputs.role.label}
                        disabled={isDisabled || !!(!organizationRole && adminProperty)}
                        isClearable={false}
                        isRequired={true}
                        isSearchable={false}
                        labelWidth={labelWidth}
                        onChange={handleOrganizationRoleChange}
                        options={axnAdminUserPropertyBundleOptions}
                        selectedOption={
                            isCustomOrgRole
                                ? { label: "Custom", value: null }
                                : selectedOrganizationRole
                        }
                    />
                    {organizationRole && (
                        <LabelValueContainer
                            controlId={`organization-user-role-description`}
                            label={" "}
                            labelIsBold={false}
                            labelWidth={labelWidth}
                            layout={LayoutTypes.ROW}
                            value={organizationRole?.description}
                        />
                    )}
                </>
            )}
            {selectedRoleType.value === AxnAdminUserPropertyBundleTypes.RP && (
                <RelyingPartyRolesContainer
                    isDisabled={isDisabled}
                    requiredUserPermissionType={UserPermissionTypes.MODIFY_USERS}
                    sessionUserHasAccessToAllAdminUserRPs={sessionUserHasAccessToAllAdminUserRPs}
                    relyingPartyRolesWithPermissions={relyingPartyRolesWithPermissions}
                    axnRelyingParties={axnRelyingParties}
                    axnAdminUserPropertyBundles={axnAdminUserPropertyBundles}
                    axnAdminUserPropertyBundleOptions={axnAdminUserPropertyBundleOptions}
                    labelWidth={labelWidth}
                    addRelyingPartyRole={addRelyingPartyRole}
                    removeRelyingPartyRole={removeRelyingPartyRole}
                    updateRelyingPartyRole={updateRelyingPartyRole}
                />
            )}
        </div>
    );
};